/**
 * QuestionEditorPage — single-question author + interactive preview.
 *
 * Promoted from the questions sandbox on 2026-07-31 (Pat); that folder was
 * deleted 2026-08-26. This IS the editor for both libraries:
 *   · `questions.edit`         → booking questions (useQuestions)
 *   · `surveys.question.edit`  → survey questions  (useSurveyQuestions)
 * The route id picks the store, the back route, the rail and the wording —
 * everything else is identical, which is the point of one editor.
 *
 * Shape follows the operator reviews of 2026-07-30 / 07-31:
 *   · Field type comes FIRST, then label, then help text.
 *   · Help text renders as a helper UNDER the input in the preview.
 *   · Required + Hidden apply to every type (QuestionCommonRules).
 *   · Text validation hides behind one toggle; no per-language rows, no tester.
 *   · The live preview is interactive — it IS the tester.
 *   · Yes/no is a Toggle: one switch, not two buttons.
 */
import { useState, useEffect, useMemo, type CSSProperties } from "react";
import { QuantityStepper, Btn, Card, Dropdown, Icon, IconBtn, T, Toggle } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { MultiSelect } from "../../shell/MultiSelect";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useQuestions, QTYPE, Q_PICKER_TYPES, type Question } from "./useQuestions";
import { useSurveyQuestions } from "../surveys/survey-questions-store";
import { QuestionTextRules } from "./QuestionTextRules";
import { QuestionCommonRules, QuestionFileRules, QuestionInfoRules, QuestionSelectRules, QuestionStepperRules, QuestionYesNoRules, infoToneStyle } from "./QuestionConfigCards";
import { validateTextAnswer, resolveErrorMessage, qTextValidation, type QTextRules } from "./text-validation";
import {
  isTextType, emptyStepper, emptyFileConfig, emptySelectConfig, emptyInfoConfig, emptyYesNoConfig,
  stepperRows, clampStep, fileAccept, fileSize, displayOptions, normalizeQuestion,
  type EditorQuestion, type QStepperConfig, type QFileConfig, type QSelectConfig, type QInfoConfig, type QYesNoConfig,
} from "./question-model";
import { useT, t as i18nT, currentLocale } from "../../lib/i18n";

const qbInput = (extra?: CSSProperties): CSSProperties => ({
  width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8,
  background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", ...extra,
});
const qbLabel = (text: string, req?: boolean) => (
  <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 }}>{text}{req ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}</div>
);

/* ══ Interactive respondent preview ═══════════════════════════════════════════
   Real state, real validation. `touched` gates the error so an untouched
   required field isn't already shouting red — the customer sees the message
   when they've had a chance to answer, not before. */

function StepperControl({ cfg, values, onChange }: { cfg: QStepperConfig; values: Record<string, number>; onChange: (id: string, n: number) => void }) {
  const t = useT();
  const rows = stepperRows(cfg);
  const btn = (disabled: boolean): CSSProperties => ({
    width: 34, height: 34, borderRadius: 999, border: `1px solid ${disabled ? T.n5 : T.n4}`,
    background: T.white, color: disabled ? T.n3 : T.n1, cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: T.font,
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => {
        const v = values[r.id] ?? clampStep(r, r.defaultValue ?? 0);
        return (
          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 10, background: T.white }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{r.label || t("questions.stepper.untitled", "Untitled count")}</span>
              {r.helper ? <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.45 }}>{r.helper}</span> : null}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <QuantityStepper
                value={v}
                onChange={(next) => onChange(r.id, clampStep(r, next))}
                min={r.min ?? undefined}
                max={r.max ?? undefined}
                step={r.step ?? undefined}
                size="sm"
                ariaLabel={r.label}
                decrementLabel={`${t("questions.stepper.decrease", "Decrease")} ${r.label}`}
                incrementLabel={`${t("questions.stepper.increase", "Increase")} ${r.label}`}
                testid={`qstep-value-${r.id}`}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RespondentField({ q }: { q: EditorQuestion }) {
  const translate = useT();
  const t = QTYPE(q.type);
  const selCfg: QSelectConfig = q.select ?? emptySelectConfig();
  const ynCfg: QYesNoConfig = q.yesno ?? emptyYesNoConfig();
  // Author order is preserved in the config; only the DISPLAY order changes.
  const opts = displayOptions(q.options ?? [], selCfg);
  const rules: QTextRules = q.textRules ?? {};
  const textual = isTextType(q.type);

  // Answer state. Re-seeded whenever the author changes the prefill/defaults, so
  // the preview always reflects what a customer would actually open.
  const stepCfg = q.stepper ?? emptyStepper();
  const seedSteps = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of stepperRows(stepCfg)) out[r.id] = clampStep(r, r.defaultValue ?? 0);
    return out;
  }, [JSON.stringify(stepCfg)]);

  const [text, setText] = useState(q.defaultValue ?? "");
  // Yes/no opens on its configured state; "unset" keeps the answer genuinely
  // absent so Required still has something to catch.
  const seedChoice = q.type === "yesno" && ynCfg.startsAs !== "unset" ? ynCfg.startsAs : null;
  const [choice, setChoice] = useState<string | null>(seedChoice);
  const [many, setMany] = useState<string[]>([]);
  const [steps, setSteps] = useState<Record<string, number>>(seedSteps);
  const [files, setFiles] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => { setText(q.defaultValue ?? ""); setTouched(false); }, [q.defaultValue, q.type]);
  useEffect(() => { setSteps(seedSteps); }, [seedSteps]);
  useEffect(() => { setChoice(seedChoice); setMany([]); setFiles([]); setTouched(false); }, [q.type, seedChoice]);

  const labelEl = (
    <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, marginBottom: 6 }}>
      {q.label || <em style={{ color: T.soft }}>{translate("questions.editorPage.questionLabel", "Question label")}</em>}
      {q.required ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}
    </div>
  );
  // Help text is a HELPER: it belongs under the control, not above it.
  const helpEl = q.help ? <div style={{ fontSize: 12, color: T.soft, marginTop: 6, lineHeight: 1.45, textWrap: "pretty" }}>{q.help}</div> : null;

  if (q.hiddenInBooking) return (
    <div>
      {labelEl}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", border: `1px dashed ${T.n4}`, borderRadius: 8, background: T.n6, fontSize: 12.5, color: T.soft }}>
        <Icon.info size={14} />
        <span>{translate("questions.editorPage.hiddenPreview", "Not shown to the customer — the stored value is still submitted.")}</span>
      </div>
      {helpEl}
    </div>
  );

  if (!t.value) {
    const info: QInfoConfig = q.info ?? emptyInfoConfig();
    const tone = infoToneStyle(info.tone);
    if (q.type === "heading") return (
      <div style={{ fontSize: 22, fontWeight: 800, color: T.n1, letterSpacing: -0.3, paddingBottom: 10, borderBottom: `2px solid ${T.border}`, textWrap: "pretty" }}>
        {q.label || translate("questions.preview.sectionHeading", "Section heading")}
      </div>
    );
    // Info text: a banner when a tone is set, a plain paragraph otherwise.
    if (!tone) return (
      <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.6, textWrap: "pretty" }}>
        {q.label || translate("questions.preview.informationalText", "Informational text")}
      </div>
    );
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 14, background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 10 }}>
        <span style={{ color: tone.fg, flexShrink: 0, marginTop: 1, display: "inline-flex" }}>{tone.icon}</span>
        <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          {info.title ? <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{info.title}</span> : null}
          <span style={{ fontSize: 13, color: T.n1, lineHeight: 1.55, textWrap: "pretty" }}>
            {q.label || translate("questions.preview.informationalText", "Informational text")}
          </span>
        </span>
      </div>
    );
  }

  const preset = qTextValidation(rules.validation);
  const ro = !!rules.readOnly || (q.type === "yesno" && ynCfg.readOnly);
  // Max length caps typing only while validation is switched on.
  const cap = rules.validationEnabled && typeof rules.maxLength === "number" && rules.maxLength > 0 ? rules.maxLength : undefined;

  // Validation — text types use the real validator; other types check required.
  const issue = textual
    ? validateTextAnswer(text, { required: q.required, rules })
    : q.required
      ? (q.type === "checks" || (q.type === "select" && selCfg.allowMultiple) || q.type === "multi"
          ? (many.length ? null : { kind: "required" as const })
        : q.type === "file" ? (files.length ? null : { kind: "required" as const })
        : q.type === "stepper" ? null
        : (choice ? null : { kind: "required" as const }))
      : null;

  const errorText = (() => {
    if (!touched || !issue) return null;
    if (issue.kind === "required") return translate("questions.preview.requiredError", "This field is required.");
    if (issue.kind === "minLength") return `${translate("questions.preview.minError", "Use at least")} ${issue.min} ${translate("questions.preview.characters", "characters")}.`;
    if (issue.kind === "maxLength") return `${translate("questions.preview.maxError", "Use at most")} ${issue.max} ${translate("questions.preview.characters", "characters")}.`;
    if (issue.kind === "badPattern") return translate("questions.preview.badPattern", "This question's pattern can't be compiled — fix it in the settings.");
    const custom = resolveErrorMessage(rules, currentLocale());
    return custom || translate("questions.preview.patternError", "This doesn't match the required format.");
  })();

  const fieldStyle = qbInput({
    ...(ro ? { background: T.n6, color: T.n3 } : {}),
    ...(errorText ? { borderColor: T.red1 } : {}),
  });
  const mark = () => setTouched(true);
  const fileCfg: QFileConfig = q.file ?? emptyFileConfig();

  const control = (() => {
    switch (q.type) {
      case "short":
        return <input value={text} readOnly={ro} maxLength={cap} onChange={(e) => { setText(e.target.value); mark(); }} onBlur={mark} placeholder={q.placeholder || preset.placeholder || ""} data-testid="preview-short" style={fieldStyle} />;
      case "long":
        return <textarea value={text} readOnly={ro} maxLength={cap} rows={3} onChange={(e) => { setText(e.target.value); mark(); }} onBlur={mark} placeholder={q.placeholder || ""} data-testid="preview-long" style={qbInput({ height: "auto", padding: 12, resize: "vertical", ...(ro ? { background: T.n6, color: T.n3 } : {}), ...(errorText ? { borderColor: T.red1 } : {}) })} />;
      case "number":
        return <input type="number" value={text} onChange={(e) => { setText(e.target.value); mark(); }} onBlur={mark} placeholder={q.placeholder || "0"} data-testid="preview-number" style={qbInput({ width: 160, ...(errorText ? { borderColor: T.red1 } : {}) })} />;
      case "stepper":
        return <StepperControl cfg={stepCfg} values={steps} onChange={(id, n) => { setSteps((s) => ({ ...s, [id]: n })); mark(); }} />;
      case "radio":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(opts.length ? opts : [{ id: "a", label: translate("questions.editorPage.optionA", "Option A") }, { id: "b", label: translate("questions.editorPage.optionB", "Option B") }]).map((o) => (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: T.n1, cursor: "pointer" }}>
                <input type="radio" name={`pv_${q.id}`} checked={choice === o.id} onChange={() => { setChoice(o.id); mark(); }} />
                {o.label}
              </label>
            ))}
          </div>
        );
      case "select":
        // One type, two cardinalities — the toggle picks the control.
        return selCfg.allowMultiple
          ? <MultiSelect options={opts.map((o) => ({ value: o.id, label: o.label }))} values={many} onChange={(v) => { setMany(v); mark(); }} placeholder={q.placeholder || translate("questions.editorPage.chooseAny", "Choose any…")} testid="preview-select" style={{ gap: 0, width: "100%" }} />
          : <Dropdown options={opts.map((o) => ({ value: o.id, label: o.label }))} value={choice} onChange={(v) => { setChoice(v); mark(); }} placeholder={q.placeholder || translate("questions.editorPage.chooseOne", "Choose one…")} testid="preview-select" style={{ gap: 0, width: "100%" }} />;
      case "yesno": {
        // A switch, not two buttons: one thing that is on or off. The state word
        // beside it carries the answer, including the neutral "not answered".
        const on = choice === "yes";
        const stateLabel = choice === null
          ? translate("questions.yesno.unanswered", "Not answered yet")
          : on ? translate("common.yes", "Yes") : translate("common.no", "No");
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 11, opacity: choice === null ? 0.75 : 1 }}>
            <Toggle
              on={on}
              disabled={ro}
              onChange={(next) => { setChoice(next ? "yes" : "no"); mark(); }}
              label={q.label || translate("questions.editorPage.questionLabel", "Question label")}
              testid="preview-yesno"
            />
            <span style={{ fontSize: 14, fontWeight: choice === null ? 500 : 600, color: choice === null ? T.soft : T.n1 }}>{stateLabel}</span>
          </div>
        );
      }
      case "checks":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(opts.length ? opts : [{ id: "a", label: translate("questions.editorPage.optionA", "Option A") }, { id: "b", label: translate("questions.editorPage.optionB", "Option B") }]).map((o) => (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: T.n1, cursor: "pointer" }}>
                <input type="checkbox" checked={many.includes(o.id)} onChange={() => { setMany((m) => m.includes(o.id) ? m.filter((x) => x !== o.id) : [...m, o.id]); mark(); }} />
                {o.label}
              </label>
            ))}
          </div>
        );
      case "multi":
        return <MultiSelect options={opts.map((o) => ({ value: o.id, label: o.label }))} values={many} onChange={(v) => { setMany(v); mark(); }} placeholder={q.placeholder || translate("questions.editorPage.chooseAny", "Choose any…")} testid="preview-multi" style={{ gap: 0, width: "100%" }} />;
      case "datetime":
        return <input type="datetime-local" value={text} onChange={(e) => { setText(e.target.value); mark(); }} data-testid="preview-datetime" style={qbInput({ width: 240, ...(errorText ? { borderColor: T.red1 } : {}) })} />;
      case "file":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ border: `1.5px dashed ${errorText ? T.red1 : T.n4}`, borderRadius: 8, padding: "18px 20px", textAlign: "center", color: T.soft, fontSize: 13, cursor: "pointer", display: "block" }}>
              <input
                type="file"
                accept={fileAccept(fileCfg) || undefined}
                multiple={fileCfg.allowMultiple}
                data-testid="preview-file"
                onChange={(e) => { setFiles([...(e.target.files ?? [])].map((f) => f.name)); mark(); }}
                style={{ display: "none" }}
              />
              <span>{translate("questions.editorPage.dragDropOr", "Drag & drop or")} </span>
              <span style={{ color: T.p1, fontWeight: 600 }}>{translate("questions.editorPage.browse", "browse")}</span>
            </label>
            <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
              {(fileCfg.formats.length ? fileCfg.formats.map((f) => f.toUpperCase()).join(" · ") : translate("questions.file.anyType", "Any file type"))}
              {` · ${translate("questions.file.upTo", "up to")} ${fileSize(fileCfg.maxSize).mb} MB`}
              {fileCfg.allowMultiple ? ` · ${translate("questions.file.severalAllowed", "several files allowed")}` : ""}
            </div>
            {files.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {files.map((n) => (
                  <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.n1 }}>
                    <Icon.attach size={12} />{n}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        );
      default:
        return <input value={text} onChange={(e) => { setText(e.target.value); mark(); }} placeholder={translate("questions.editorPage.answer", "Answer…")} style={fieldStyle} />;
    }
  })();

  return (
    <div>
      {labelEl}
      {control}
      {helpEl}
      {errorText ? (
        <div role="alert" data-testid="preview-error" style={{ fontSize: 12, color: T.red1, marginTop: 6, lineHeight: 1.45, fontWeight: 500 }}>{errorText}</div>
      ) : null}
      {ro ? (
        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Icon.lock size={11} />{translate("questions.textRules.readOnly", "Read-only")}
        </div>
      ) : null}
    </div>
  );
}

/* ═══ Main editor ═══════════════════════════════════════════════════════ */
export default function QuestionEditorPage() {
  const translate = useT();
  const nav = useNav();
  // The SAME editor serves booking questions and survey questions; the route id
  // selects which library it writes to and where it returns.
  const isSurvey = nav.route === "surveys.question.edit";
  const bookingStore = useQuestions();
  const surveyStore = useSurveyQuestions();
  const store = isSurvey ? surveyStore : bookingStore;
  const backRoute = isSurvey ? "surveys.questions" : "questions";
  const editRoute = isSurvey ? "surveys.question.edit" : "questions.edit";
  const railId = isSurvey ? "surveys" : "cs-questions";
  const backLabel = isSurvey ? translate("questions.editorPage.surveyQuestions", "Survey questions") : translate("questions.library.questions", "Questions");
  const isNew = !!(nav.params?.new);
  const viewOnly = !!(nav.params?.view);
  const id = nav.params?.id as string | undefined;
  const canWrite = !viewOnly && can("questions.write");

  const seed = (): EditorQuestion => normalizeQuestion(isNew ? store.newQuestion() : (id && store.getQ(id) ? JSON.parse(JSON.stringify(store.getQ(id))) : store.newQuestion()));
  const [form, setForm] = useState<EditorQuestion>(seed);
  const routeKey = `${isNew ? "new" : id ?? ""}:${viewOnly ? "v" : "e"}`;
  useEffect(() => { setForm(seed()); }, [routeKey]);

  const t = QTYPE(form.type);
  const set = (patch: Partial<EditorQuestion>) => setForm((f) => ({ ...f, ...patch }));
  const changeType = (tid: string) => {
    const nt = QTYPE(tid);
    setForm((f) => {
      const next: EditorQuestion = { ...f, type: tid };
      if (nt.opts && (!f.options || f.options.length === 0)) next.options = [store.newOption("Option 1"), store.newOption("Option 2")];
      if (tid === "stepper" && !f.stepper) next.stepper = emptyStepper();
      if (tid === "select" && !f.select) next.select = emptySelectConfig();
      if (tid === "yesno" && !f.yesno) next.yesno = emptyYesNoConfig();
      if (tid === "label" && !f.info) next.info = emptyInfoConfig();
      if (tid === "file" && !f.file) next.file = emptyFileConfig();
      return next;
    });
  };
  const setOpt = (oid: string, label: string) => setForm((f) => ({ ...f, options: f.options.map((o) => o.id === oid ? { ...o, label } : o) }));
  const addOpt = () => setForm((f) => ({ ...f, options: [...(f.options || []), store.newOption("")] }));
  const rmOpt = (oid: string) => setForm((f) => ({ ...f, options: f.options.filter((o) => o.id !== oid) }));

  const saveDisabled = !form.label.trim() || (t.opts && (form.options || []).filter((o) => o.label.trim()).length < 2);
  const save = async () => {
    const clean = { ...form, options: t.opts ? form.options.filter((o) => o.label.trim()) : (form.options || []) };
    try {
      await store.upsertQ(clean as Question);
      toast({
        tone: "success",
        title: isNew ? translate("questions.editorPage.questionCreated", "Question created") : translate("questions.editorPage.questionSaved", "Question saved"),
        subtitle: isSurvey ? translate("questions.editorPage.availableForUseInSurveys", "Available for use in surveys.") : translate("questions.editorPage.availableForUseInEventsAndGroups", "Available for use in events and groups."),
      });
      nav.go(backRoute);
    } catch (e) {
      console.warn("save question failed:", e);
      toast({ tone: "danger", title: translate("questions.editorPage.couldnTSaveQuestion", "Couldn't save question"), subtitle: translate("questions.editorPage.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };

  const newTitle = isSurvey
    ? translate("questions.editorPage.newSurveyQuestion", "New survey question")
    : translate("questions.editorPage.newBookingQuestion", "New booking question");

  return (
    <Screen rail={railId}>
      <PageHeader
        title={isNew ? newTitle : (form.label || translate("events.creationCreateEventRegistration.question", "Question"))}
        subtitle={viewOnly
          ? translate("questions.editorPage.subtitleView", "Read-only view of a question in the shared library.")
          : isNew
            ? translate("questions.editorPage.subtitleNew", "Set the field up on the left; the preview on the right is live — fill it in to test the rules.")
            : translate("questions.editorPage.subtitleEdit", "Edits apply everywhere this question is already used.")}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go(backRoute)}><Icon.arrow dir="left" size={11}/> {backLabel}</span>}
        actions={viewOnly ? <Btn icon={<Icon.edit size={14}/>} onClick={() => nav.go(editRoute, { id: form.id })}>{translate("common.actions.edit", "Edit")}</Btn> : <>
          <Btn variant="secondary" onClick={() => nav.go(backRoute)}>{translate("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14}/>} disabled={saveDisabled} onClick={save}>{isNew ? translate("questions.questionEditorPage.create", "Create") : translate("common.actions.save", "Save")}</Btn>
        </>}
      />
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(440px,1fr) minmax(360px,460px)", minHeight: 0, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
        {/* Settings panel — field type first, then naming, then behaviour, then
            the type's own config (operator order, 2026-07-30). */}
        <div style={{ borderRight: `1px solid ${T.border}`, overflow: "auto", padding: "20px 24px 40px", background: T.canvas, opacity: canWrite ? 1 : 0.7, pointerEvents: canWrite ? "auto" : "none" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, margin: "0 2px 8px" }}>{translate("questions.editorPage.fieldType", "Field type")}</div>
          {/* One grouped dropdown instead of a 12-button grid (operator,
              2026-07-30). The DS Dropdown has no optgroup, so each group header
              is a disabled option — unpickable, and rendered muted by the
              component's own disabled styling. Each type carries its hint as
              supporting text, which the old grid only exposed as a tooltip. */}
          <Dropdown
            options={["Layout", "Text", "Choice", "Special"].flatMap((cat) => [
              { value: `__group_${cat}`, label: cat.toUpperCase(), disabled: true },
              ...Q_PICKER_TYPES.filter((x) => x.group === cat).map((x) => ({
                value: x.id,
                label: x.label,
                supportingText: x.hint,
              })),
            ])}
            value={form.type}
            onChange={(v) => changeType(v)}
            aria-label={translate("questions.editorPage.fieldType", "Field type")}
            testid="question-field-type"
            style={{ gap: 0, width: "100%" }}
          />

          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
            <div>{qbLabel(translate("questions.editorPage.questionLabel", "Question label"), true)}<input autoFocus={isNew} value={form.label} onChange={(e) => set({ label: e.target.value })} placeholder={translate("questions.editorPage.eGWhatSYourDietaryPreference", "e.g. What's your dietary preference?")} style={qbInput()}/></div>
            <div>{qbLabel(translate("questions.editorPage.helpText", "Help text"))}<input value={form.help} onChange={(e) => set({ help: e.target.value })} placeholder={translate("questions.editorPage.optionalGuidanceShownUnderTheQuestion", "Optional guidance shown under the question")} style={qbInput()}/></div>
          </Card>

          {t.value ? (
            <QuestionCommonRules
              required={form.required}
              hidden={!!form.hiddenInBooking}
              onRequired={(next) => set({ required: next })}
              onHidden={(next) => set({ hiddenInBooking: next })}
            />
          ) : null}

          {t.opts ? (
            <Card style={{ padding: 16, marginTop: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{translate("questions.editorPage.options", "Options")}</div>
                <Btn variant="secondary" size="sm" icon={<Icon.plus size={13}/>} onClick={addOpt}>{translate("questions.editorPage.addOption", "Add option")}</Btn>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(form.options || []).map((o, i) => (
                  <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: T.soft, fontSize: 12, width: 16 }}>{i + 1}</span>
                    <input value={o.label} onChange={(e) => setOpt(o.id, e.target.value)} placeholder={i18nT("questions.editorPage.optionI1.icu", { value0: i + 1 })} style={qbInput({ height: 36 })}/>
                    <IconBtn size={30} label={translate("common.actions.remove", "Remove")} onClick={() => rmOpt(o.id)}><Icon.x size={13}/></IconBtn>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {isTextType(form.type) ? (
            <QuestionTextRules
              type={form.type}
              hidden={!!form.hiddenInBooking}
              rules={form.textRules ?? {}}
              placeholder={form.placeholder ?? ""}
              defaultValue={form.defaultValue ?? ""}
              onChange={(next) => set({ textRules: next })}
              onPlaceholder={(next) => set({ placeholder: next })}
              onDefaultValue={(next) => set({ defaultValue: next })}
            />
          ) : null}

          {form.type === "label" ? (
            <QuestionInfoRules cfg={form.info ?? emptyInfoConfig()} onChange={(next) => set({ info: next })} />
          ) : null}

          {form.type === "select" ? (
            <QuestionSelectRules
              cfg={form.select ?? emptySelectConfig()}
              optionCount={(form.options ?? []).length}
              placeholder={form.placeholder ?? ""}
              onPlaceholder={(next) => set({ placeholder: next })}
              onChange={(next) => set({ select: next })}
            />
          ) : null}

          {form.type === "yesno" ? (
            <QuestionYesNoRules cfg={form.yesno ?? emptyYesNoConfig()} required={form.required} onChange={(next) => set({ yesno: next })} />
          ) : null}

          {form.type === "file" ? (
            <QuestionFileRules cfg={form.file ?? emptyFileConfig()} onChange={(next) => set({ file: next })} />
          ) : null}

          {form.type === "stepper" ? (
            <QuestionStepperRules cfg={form.stepper ?? emptyStepper()} onChange={(next) => set({ stepper: next })} />
          ) : null}
        </div>

        {/* Preview panel */}
        <div style={{ overflow: "auto", padding: 24, background: T.canvas, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5 }}>{translate("questions.editorPage.livePreview", "Live preview")}</span>
            <span style={{ fontSize: 11.5, color: T.soft }}>{translate("questions.preview.tryIt", "· fill it in to test the rules")}</span>
          </div>
          <Card style={{ padding: 20 }}>
            {/* Keyed on type AND the select cardinality so switching either resets
                the answer state instead of carrying a stale value across. */}
            <RespondentField key={`${form.type}:${form.select?.allowMultiple ? "m" : "1"}`} q={{ ...form, options: form.options || [] }}/>
          </Card>
          {t.branch ? (
            <div style={{ padding: 12, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.5, display: "flex", gap: 8 }}>
              <span style={{ color: T.p1, flexShrink: 0 }}><Icon.network size={14}/></span>
              <span>{translate("questions.editorPage.answersToThisFieldCan", "Answers to this field can")}{" "}<b>{translate("questions.editorPage.driveBranching", "drive branching")}</b> {translate("questions.editorPage.setTheIfThisThenThatRulesWhenYouAddItTo", "— set the \"if-this-then-that\" rules when you add it to a group.")}</span>
            </div>
          ) : null}
        </div>
      </div>
    </Screen>
  );
}
